import type { JsonObject, JsonValue } from '@bufbuild/protobuf';
import { timestampDate } from '@bufbuild/protobuf/wkt';
import type { AuditEvent } from '@sunstone/api-client/gen/audit/v1/audit_pb';
import { Badge, Button } from '@sunstone/ui';
import { ChevronRight, Clock, ExternalLink, Settings } from 'lucide-react';
import { Fragment, useState } from 'react';
import { relativeTime } from '@/lib/time';

function eventVerb(eventType: string): string {
  const verb = eventType.split('.').slice(1).join('.').replaceAll('_', ' ');
  return verb || eventType;
}

function verbClassName(et: string): string {
  if (
    et.includes('created') ||
    et.includes('invited') ||
    et.includes('completed') ||
    et.includes('allowed') ||
    et.includes('enabled')
  )
    return 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300';
  if (
    et.includes('deleted') ||
    et.includes('deactivated') ||
    et.includes('disabled') ||
    et.includes('failed') ||
    et.includes('denied')
  )
    return 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300';
  if (et.includes('status_changed'))
    return 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300';
  return 'bg-muted text-muted-foreground';
}

// Keys excluded from the params panel — always the current resource, noise, or restricted to super-admin.
const HIDDEN_PARAMS = new Set([
  'DeploymentID',
  'WorkspaceID',
  'TenantID',
  'UserID',
  'WorkflowRunID', // internal wrun_ ID, not useful to operators
  'Kind', // always "workspace"
  'TemporalWorkflowURL', // rendered as icon link
  'RunURL', // rendered as icon link
  'ErrorTail', // rendered as error output block
  'ErrorMessage', // rendered as error output block
]);

function filterParams(params: JsonObject): [string, JsonValue][] {
  return Object.entries(params).filter(([k, v]) => !HIDDEN_PARAMS.has(k) && v !== null && v !== '');
}

const PROVISIONING_COMPLETED = new Set([
  'workspace.infra_provisioning_completed',
  'workspace.centxforge_installation_completed',
  'workspace.new_installation_completed',
  'workspace.provisioning_completed', // legacy
]);

function badgeInfo(et: string, params?: JsonObject): { cls: string; label: string } {
  if (params) {
    const c = params['Conclusion'] as string | undefined;
    if (c === 'failure')
      return {
        cls: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
        label: PROVISIONING_COMPLETED.has(et) ? 'Provisioning failure' : eventVerb(et),
      };
    if (c === 'cancelled')
      return {
        cls: 'bg-muted text-muted-foreground',
        label: PROVISIONING_COMPLETED.has(et) ? 'Provisioning cancelled' : eventVerb(et),
      };
  }
  if (PROVISIONING_COMPLETED.has(et) && params) {
    const c = params['Conclusion'] as string | undefined;
    if (c === 'success')
      return {
        cls: 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300',
        label: 'Provisioning success',
      };
  }
  return { cls: verbClassName(et), label: eventVerb(et) };
}

function jsonValueToString(v: JsonValue): string {
  if (v !== null && typeof v === 'object') return JSON.stringify(v);
  return String(v as string | number | boolean);
}

// ─── Param renderers ─────────────────────────────────────────────────────────

function ParamValue({ value }: Readonly<{ value: JsonValue }>) {
  if (Array.isArray(value)) {
    // Array of primitives (e.g. NameServers)
    if (value.every((v) => typeof v === 'string' || typeof v === 'number')) {
      return (
        <div className="flex flex-col gap-0.5">
          {value.map((v) => {
            const str = jsonValueToString(v);
            return (
              <span key={str} className="font-mono text-xs text-foreground/80">
                {str}
              </span>
            );
          })}
        </div>
      );
    }

    // Array of objects (e.g. Quotas, FailedResources)
    return (
      <div className="flex flex-col gap-1.5 w-full">
        {(value as JsonObject[]).map((obj) => {
          const objKey = Object.values(obj).slice(0, 2).map(jsonValueToString).join('-');
          return (
            <div
              key={objKey}
              className="grid gap-x-3 gap-y-0.5 rounded bg-muted/40 px-2 py-1.5 text-xs"
              style={{ gridTemplateColumns: 'auto 1fr' }}
            >
              {Object.entries(obj).map(([k, v]) => (
                <Fragment key={k}>
                  <span className="text-muted-foreground/70 font-medium whitespace-nowrap">
                    {k}
                  </span>
                  <span className="font-mono text-foreground/80 break-all">
                    {jsonValueToString(v)}
                  </span>
                </Fragment>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  // Plain object (e.g. NameChange, RoleChange on a combined update event)
  if (value !== null && typeof value === 'object') {
    const obj = value as JsonObject;
    return (
      <div
        className="grid gap-x-3 gap-y-0.5 rounded bg-muted/40 px-2 py-1.5 text-xs w-full"
        style={{ gridTemplateColumns: 'auto 1fr' }}
      >
        {Object.entries(obj).map(([k, v]) => (
          <Fragment key={k}>
            <span className="text-muted-foreground/70 font-medium whitespace-nowrap">{k}</span>
            <span className="font-mono text-foreground/80 break-all">{jsonValueToString(v)}</span>
          </Fragment>
        ))}
      </div>
    );
  }

  return (
    <span className="font-mono text-xs text-foreground/80 break-all">
      {jsonValueToString(value)}
    </span>
  );
}

function ParamsPanel({ params }: Readonly<{ params: JsonObject }>) {
  const entries = filterParams(params);
  const errorOutput =
    (params['ErrorTail'] ? String(params['ErrorTail']) : null) ??
    (params['ErrorMessage'] ? String(params['ErrorMessage']) : null);
  if (entries.length === 0 && !errorOutput) return null;

  return (
    <div className="ml-7 mt-1 mb-1 rounded-md border border-border/50 bg-muted/20 divide-y divide-border/30 overflow-hidden">
      {entries.map(([key, value]) => (
        <div key={key} className="flex gap-3 px-3 py-1.5 items-center">
          <span className="text-xs text-muted-foreground font-medium w-36 shrink-0">
            {key
              .replaceAll(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
              .replaceAll(/([a-z])([A-Z])/g, '$1 $2')
              .trim()}
          </span>
          <div className="flex-1 min-w-0 flex items-center">
            <ParamValue value={value} />
          </div>
        </div>
      ))}
      {errorOutput && (
        <div className="px-3 py-2">
          <p className="text-xs text-muted-foreground font-medium mb-1.5">Error output</p>
          <pre className="rounded border border-border/40 bg-background p-2.5 text-xs font-mono text-foreground/80 overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
            {errorOutput}
          </pre>
        </div>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

interface AuditHistoryProps {
  readonly events: AuditEvent[];
  readonly onLoadMore?: () => void;
  readonly hasMore?: boolean;
  readonly isLoadingMore?: boolean;
}

export function AuditHistory({ events, onLoadMore, hasMore, isLoadingMore }: AuditHistoryProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const init = new Set<string>();
    for (const e of events) {
      if (e.params?.['Conclusion'] === 'failure') init.add(e.id);
    }
    return init;
  });

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
        <Clock className="w-8 h-8 opacity-30" />
        <p className="text-sm">No history recorded yet.</p>
      </div>
    );
  }

  function toggle(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  return (
    <div className="flow-root">
      <ul className="divide-y divide-border/60">
        {events.map((event) => {
          const date = event.createdAt ? timestampDate(event.createdAt) : new Date(0);
          const isSystem = event.actorKind === 'system';
          const actor = isSystem ? 'System' : event.actorId || 'System';
          const hasParams =
            event.params &&
            (filterParams(event.params).length > 0 ||
              Boolean(event.params['ErrorTail']) ||
              Boolean(event.params['ErrorMessage']));
          const isExpanded = expandedIds.has(event.id);
          const badge = badgeInfo(event.eventType, event.params ?? undefined);

          return (
            <li key={event.id} className="hover:bg-muted/20 transition-colors">
              <div className="flex items-start gap-4 py-3.5 px-4">
                {/* Timeline dot / system icon */}
                {isSystem ? (
                  <Settings className="mt-1 shrink-0 w-3.5 h-3.5 text-muted-foreground/60" />
                ) : (
                  <div className="mt-1.5 shrink-0 w-2 h-2 rounded-full bg-border ring-2 ring-background" />
                )}

                {/* Main content */}
                <div className="flex-1 min-w-0 space-y-0.5">
                  <p className="text-sm text-foreground leading-snug">{event.description}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={badge.cls}>{badge.label}</Badge>
                    {/* TODO: gate these links on operator role as part of RBAC work —
                        partner users cannot access GitHub Actions or internal Temporal Web UI */}
                    {(event.params?.['TemporalWorkflowURL'] || event.params?.['RunURL']) && (
                      <span className="flex items-center gap-1.5">
                        {event.params?.['TemporalWorkflowURL'] && (
                          <a
                            href={String(event.params['TemporalWorkflowURL'])}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground"
                          >
                            <ExternalLink className="w-3 h-3" /> Temporal
                          </a>
                        )}
                        {event.params?.['RunURL'] && (
                          <a
                            href={String(event.params['RunURL'])}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground"
                          >
                            <ExternalLink className="w-3 h-3" /> GitHub run
                          </a>
                        )}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">by {actor}</span>
                  </div>
                </div>

                {/* Timestamp + expand button */}
                <div className="shrink-0 flex items-center gap-1.5">
                  <time
                    dateTime={date.toISOString()}
                    title={date.toLocaleString()}
                    className="text-xs text-muted-foreground whitespace-nowrap"
                  >
                    {relativeTime(date)}
                  </time>
                  {hasParams && (
                    <button
                      type="button"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Hide details' : 'Show details'}
                      onClick={() => toggle(event.id)}
                      className="rounded p-0.5 text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted transition-colors"
                    >
                      <ChevronRight
                        className={`w-3.5 h-3.5 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                      />
                    </button>
                  )}
                </div>
              </div>

              {/* Expandable params panel */}
              {hasParams && isExpanded && event.params && (
                <div className="px-4 pb-3">
                  <ParamsPanel params={event.params} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <div className="flex justify-center py-4">
          <Button variant="outline" size="sm" onClick={onLoadMore} disabled={isLoadingMore}>
            {isLoadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  );
}
